import { useMemo } from 'react';
import { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { usePreference } from '@frontend/user-preferences';
import { useControlledState, useReorderDropHandler, useReorderedList } from '../../../../hooks';
import { CalendarEntityType } from '../../../../stores';
import { CALENDAR_COLUMN_DRAG_DROP, DRAG_PENDO_EVENT_ENTRY_POINTS } from '../../constants';
import { useColumnEntityReorderStore } from '../../providers';
import { LocationWithCalendarColumnEntities } from '../../types';
import { useAddNewOrderToQueue } from './use-add-neworder-to-queue';
import { useDraggedItem } from './use-dragged-item';
import { usePendoTrackingForDragDrop } from './use-pendo-tracking-for-drap-drop';

/**
 * Hook to get reordered column entities based on user preferences for a single location.
 * @param location location with calendar column entities
 * @param calendarEntityType calendar entity type (provider/operatory)
 * @returns reordered list of column entities along with drag and drop props
 */
export const useReorderedColumnEntities = (
  location: LocationWithCalendarColumnEntities,
  calendarEntityType: CalendarEntityType
) => {
  const [columnEntityOrder] = usePreference('ScheduleCalendarViewColumnOrder');
  const { setIsInReorderMode } = useColumnEntityReorderStore(['setIsInReorderMode']);

  // Get the reordered list based on user preferences and
  // use a controlled state to manage the list locally during drag and drop
  const listOrderIdMap = useMemo(
    () => columnEntityOrder?.[calendarEntityType]?.[location.id] ?? {},
    [columnEntityOrder, calendarEntityType, location.id]
  );
  const { reorderedList: initialReorderedList } = useReorderedList({
    list: location.columnEntities,
    listOrderIdMap,
    idKey: 'id',
  });
  const [list, setList] = useControlledState(initialReorderedList);

  const { draggedItem, dragHandlers } = useDraggedItem(list);

  const resetList = () => {
    setList(initialReorderedList);
  };

  const { addNewOrderToQueue } = useAddNewOrderToQueue({
    locationId: location.id,
    calendarEntityType,
    setList,
    resetList,
  });
  const { onDragEnd: onDragEndReorderHandler } = useReorderDropHandler({
    list,
    onChange: (newOrderIdMap, newList) => {
      addNewOrderToQueue(newOrderIdMap, newList);
    },
    listOrderIdMap,
  });

  const trackDragStartEvent = usePendoTrackingForDragDrop({
    eventName: 'schedule_column_reordered',
    entryPoint: DRAG_PENDO_EVENT_ENTRY_POINTS.drag,
  });

  const onDragStart = (e: DragStartEvent) => {
    setIsInReorderMode(true);
    dragHandlers.onDragStart(e.active.id as string);
    trackDragStartEvent();
  };

  const onDragEnd = (e: DragEndEvent) => {
    onDragEndReorderHandler(e);
    dragHandlers.onDragEnd();
  };

  return {
    list,
    setList,
    resetList,
    draggedItem,
    dragDropContextProps: { onDragEnd, onDragStart },
    droppableProps: {
      id: CALENDAR_COLUMN_DRAG_DROP.getDroppableId(location.id, calendarEntityType),
      type: CALENDAR_COLUMN_DRAG_DROP.getDroppableType(calendarEntityType),
      direction: 'horizontal' as const,
    },
  };
};
